import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  NativeSelect,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { RefreshCwIcon, ShieldAlertIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { PageHeader } from '~/components/shell'
import { useAuth } from '~/contexts/AuthContext'
import { auditApi } from '~/lib/api'

export const Route = createFileRoute('/_app/admin/audit')({
  component: AuditPage,
})

const ACTION_COLORS: Record<string, string> = {
  USER_LOGIN: 'green',
  USER_LOGOUT: 'gray',
  ROLE_CHANGE: 'orange',
  USER_STATUS_CHANGE: 'yellow',
  SETTING_UPDATE: 'blue',
  POD_RESTART: 'red',
  POD_LOGS_VIEW: 'purple',
  AI_QUERY: 'cyan',
}

function AuditPage() {
  const { hasMinRole, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [action, setAction] = useState('')
  const [resourceType, setResourceType] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 25

  const isAdmin = hasMinRole('admin')

  // Redirect non-admins after a short delay
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      const timer = setTimeout(() => {
        navigate({ to: '/' })
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [authLoading, isAdmin, navigate])

  // Show access denied for non-admins
  if (!authLoading && !isAdmin) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Box p={4} borderRadius="full" bg="danger.subtle">
            <ShieldAlertIcon size={48} color="var(--chakra-colors-red-500)" />
          </Box>
          <Heading size="lg" color="danger.fg">
            Access Denied
          </Heading>
          <Text color="content.secondary" maxW="400px">
            This page is restricted to <strong>admin</strong> users only.
          </Text>
          <Text fontSize="sm" color="content.secondary">
            Redirecting to home page...
          </Text>
        </VStack>
      </Flex>
    )
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['audit', action, resourceType, page],
    queryFn: () =>
      auditApi.list({
        action: action || undefined,
        resourceType: resourceType || undefined,
        page,
        pageSize,
      }),
    retry: false,
  })

  const logs = data?.logs || []
  const total = data?.total || 0
  const totalPages = Math.ceil(total / pageSize)

  const clearFilters = () => {
    setAction('')
    setResourceType('')
    setPage(1)
  }

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Audit Logs"
        subtitle="View system activity and user actions"
        actions={
          <Button size="sm" onClick={() => refetch()}>
            <RefreshCwIcon size={16} />
            Refresh
          </Button>
        }
      />

      <VStack align="stretch" gap={6}>
        <Card.Root p={4}>
          <HStack gap={4} flexWrap="wrap">
            <NativeSelect.Root size="sm" w="200px">
              <NativeSelect.Field
                value={action}
                onChange={(e) => {
                  setAction(e.target.value)
                  setPage(1)
                }}
              >
                <option value="">All actions</option>
                <option value="USER_LOGIN">User Login</option>
                <option value="USER_LOGOUT">User Logout</option>
                <option value="ROLE_CHANGE">Role Change</option>
                <option value="USER_STATUS_CHANGE">Status Change</option>
                <option value="SETTING_UPDATE">Setting Update</option>
                <option value="POD_RESTART">Pod Restart</option>
                <option value="POD_LOGS_VIEW">Pod Logs View</option>
                <option value="AI_QUERY">AI Query</option>
              </NativeSelect.Field>
            </NativeSelect.Root>

            <NativeSelect.Root size="sm" w="200px">
              <NativeSelect.Field
                value={resourceType}
                onChange={(e) => {
                  setResourceType(e.target.value)
                  setPage(1)
                }}
              >
                <option value="">All resources</option>
                <option value="USER">User</option>
                <option value="SETTING">Setting</option>
                <option value="POD">Pod</option>
                <option value="AI">AI</option>
              </NativeSelect.Field>
            </NativeSelect.Root>

            {(action || resourceType) && (
              <Button size="sm" variant="ghost" onClick={clearFilters}>
                Clear filters
              </Button>
            )}

            <Text fontSize="sm" color="content.secondary" ml="auto">
              {total} total entries
            </Text>
          </HStack>
        </Card.Root>

        <Card.Root>
          <Table.Root>
            <Table.Header>
              <Table.Row bg="bg.subtle">
                <Table.ColumnHeader>Timestamp</Table.ColumnHeader>
                <Table.ColumnHeader>User</Table.ColumnHeader>
                <Table.ColumnHeader>Action</Table.ColumnHeader>
                <Table.ColumnHeader>Resource</Table.ColumnHeader>
                <Table.ColumnHeader>Details</Table.ColumnHeader>
                <Table.ColumnHeader>IP Address</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <Flex justify="center" py={8}>
                      <Spinner />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : logs.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <Text textAlign="center" py={8} color="content.secondary">
                      No audit logs found
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                logs.map((log) => (
                  <Table.Row key={log.id}>
                    <Table.Cell>
                      <Text fontSize="sm">{new Date(log.createdAt).toLocaleString()}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{log.userEmail || 'System'}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette={ACTION_COLORS[log.action] || 'gray'} variant="subtle">
                        {log.action}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <VStack align="flex-start" gap={0}>
                        <Text fontSize="sm">{log.resourceType || '-'}</Text>
                        {log.resourceId && (
                          <Text fontSize="xs" color="content.secondary" fontFamily="mono">
                            {log.resourceId}
                          </Text>
                        )}
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        fontSize="xs"
                        fontFamily="mono"
                        color="content.secondary"
                        maxW="300px"
                        truncate
                      >
                        {log.details ? JSON.stringify(log.details) : '-'}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm" fontFamily="mono">
                        {log.ipAddress || '-'}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card.Root>

        {totalPages > 1 && (
          <Flex justify="center" gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Text alignSelf="center" fontSize="sm">
              Page {page} of {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </Flex>
        )}
      </VStack>
    </Box>
  )
}
